import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { Newspaper, Plus, Edit, Trash2, X, Sparkles, Loader2, Globe, Upload, Image as ImageIcon } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import SERPPreview from "@/components/SERPPreview";

const EMPTY = {
  title: "", slug: "", excerpt: "", content: "",
  cover_image: "", author: "",
  tags: [], related_product_ids: [], seo_title: "", seo_description: "",
  published: true,
};

function slugify(s) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function TagInput({ values, setValues, placeholder, testid }) {
  const [draft, setDraft] = useState("");
  return (
    <div>
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); if (draft.trim()) { setValues([...(values || []), draft.trim()]); setDraft(""); } }
          }}
          data-testid={testid}
        />
        <Button type="button" variant="outline" size="sm" onClick={() => { if (draft.trim()) { setValues([...(values || []), draft.trim()]); setDraft(""); } }}><Plus className="w-4 h-4" /></Button>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {(values || []).map((v, i) => (
          <Badge key={i} variant="secondary" className="gap-1 pr-1">{v}
            <button type="button" onClick={() => setValues(values.filter((_, j) => j !== i))}><X className="w-3 h-3 ml-1" /></button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

function PostForm({ initial, onSubmit, onCancel, allProducts }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiWordCount, setAiWordCount] = useState(700);
  const [aiLoading, setAiLoading] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const runAiDraft = async () => {
    if (!aiTopic.trim()) return toast.error("Enter a topic first");
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/blog/ai-draft", { topic: aiTopic.trim(), word_count: Number(aiWordCount) || 700 });
      setForm({
        ...form,
        title: data.title || form.title,
        slug: data.slug || form.slug,
        excerpt: data.excerpt || form.excerpt,
        content: data.content || form.content,
        seo_title: data.seo_title || form.seo_title,
        seo_description: data.seo_description || form.seo_description,
        tags: [...new Set([...(form.tags || []), ...(data.tags || [])])],
      });
      toast.success("Draft generated — review and edit before publishing");
      setAiOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "AI draft failed");
    } finally {
      setAiLoading(false);
    }
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    if (!form.slug.trim()) return toast.error("Slug required");
    if (!form.excerpt.trim()) return toast.error("Excerpt required");
    if (!form.content.trim()) return toast.error("Content required");
    setSaving(true);
    try { await onSubmit(form); } finally { setSaving(false); }
  };

  const toggleRelated = (pid) => {
    const cur = form.related_product_ids || [];
    if (cur.includes(pid)) set("related_product_ids", cur.filter((x) => x !== pid));
    else set("related_product_ids", [...cur, pid]);
  };

  return (
    <div className="space-y-4 py-4">
      {/* AI Article Writer */}
      <div className="border border-teal/40 rounded-xl p-4 bg-gradient-to-br from-secondary/60 to-white" data-testid="ai-draft-panel">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            <div className="w-9 h-9 rounded-lg bg-teal text-white flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <div className="font-display text-lg">AI Article Writer</div>
              <div className="text-xs text-muted-foreground">Give a topic — get title, excerpt, tags, SEO meta &amp; a full Markdown draft in ~15 s.</div>
            </div>
          </div>
          {!aiOpen && (
            <Button type="button" variant="outline" onClick={() => setAiOpen(true)} data-testid="ai-draft-open">
              <Sparkles className="w-4 h-4 mr-2" /> Draft with AI
            </Button>
          )}
        </div>
        {aiOpen && (
          <div className="mt-3 grid sm:grid-cols-[1fr_120px_auto] gap-2 items-end">
            <div>
              <Label className="text-xs">Topic / working title</Label>
              <Input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} placeholder="e.g. Best time to visit Kashmir from Hyderabad — month by month" data-testid="ai-draft-topic" />
            </div>
            <div>
              <Label className="text-xs">Word count</Label>
              <Input type="number" min="300" max="2000" value={aiWordCount} onChange={(e) => setAiWordCount(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setAiOpen(false)} disabled={aiLoading}>Cancel</Button>
              <Button type="button" onClick={runAiDraft} disabled={aiLoading} className="bg-saffron hover:bg-saffron-dark text-white" data-testid="ai-draft-generate">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {aiLoading ? "Writing..." : "Generate"}
              </Button>
            </div>
          </div>
        )}
        {aiOpen && <p className="text-xs text-muted-foreground mt-2">Existing content will be replaced. Tags will be merged with any you already added.</p>}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label>Title *</Label>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} onBlur={() => !form.slug && set("slug", slugify(form.title))} data-testid="blog-title" />
        </div>
        <div>
          <Label>Slug *</Label>
          <Input value={form.slug} onChange={(e) => set("slug", slugify(e.target.value))} data-testid="blog-slug" />
        </div>
        <div>
          <Label>Author</Label>
          <Input value={form.author} onChange={(e) => set("author", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <Label>Cover Image</Label>
          <div className="flex gap-2">
            <Input value={form.cover_image || ""} onChange={(e) => set("cover_image", e.target.value)} placeholder="https://..." className="flex-1" data-testid="blog-cover" />
            <label className="shrink-0">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="blog-cover-upload-input"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const fd = new FormData();
                  fd.append("file", file);
                  try {
                    const res = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    set("cover_image", res.data.url);
                    toast.success("Cover image uploaded");
                  } catch { toast.error("Upload failed"); }
                  e.target.value = "";
                }}
              />
              <span className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border border-border bg-background hover:bg-muted cursor-pointer transition-colors" data-testid="blog-cover-upload-btn">
                <Upload className="w-4 h-4" /> Upload
              </span>
            </label>
          </div>
          {form.cover_image && <img src={form.cover_image} alt="preview" className="mt-2 max-h-32 rounded" />}
        </div>
        <div className="sm:col-span-2">
          <Label>Excerpt * (shown on listing)</Label>
          <Textarea rows={2} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} data-testid="blog-excerpt" />
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-1">
          <Label>Content (Markdown) *</Label>
          <button type="button" onClick={() => setPreview(!preview)} className="text-xs text-teal underline" data-testid="blog-toggle-preview">{preview ? "Edit" : "Preview"}</button>
        </div>
        {preview ? (
          <div className="border border-border rounded-lg p-4 bg-secondary/20 prose max-w-none prose-headings:font-display">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content || "*Nothing to preview*"}</ReactMarkdown>
          </div>
        ) : (
          <Textarea rows={14} value={form.content} onChange={(e) => set("content", e.target.value)} className="font-mono text-sm" placeholder="## Heading&#10;Write in **Markdown**..." data-testid="blog-content-input" />
        )}
        <p className="text-xs text-muted-foreground mt-1">Supports Markdown: **bold**, *italic*, # headings, [links](url), lists, tables.</p>
      </div>

      <div>
        <Label>Tags</Label>
        <TagInput values={form.tags} setValues={(v) => set("tags", v)} placeholder="staycation, weekend..." testid="blog-tags" />
      </div>

      <div>
        <Label>Related products ({form.related_product_ids?.length || 0} selected)</Label>
        <div className="max-h-48 overflow-y-auto border border-border rounded-lg p-2 space-y-1">
          {(allProducts || []).slice(0, 60).map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-secondary/40 p-1 rounded">
              <input type="checkbox" checked={form.related_product_ids?.includes(p.id) || false} onChange={() => toggleRelated(p.id)} />
              <span className="flex-1 truncate">{p.title}</span>
              <span className="text-xs text-muted-foreground capitalize">{p.category}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-3 border border-border rounded-xl p-4">
        <h3 className="font-semibold flex items-center gap-2 text-sm"><Globe className="w-4 h-4 text-primary" /> SEO</h3>
        <SERPPreview title={form.seo_title || form.title} description={form.seo_description || form.excerpt} slug={form.slug ? `blog/${form.slug}` : ""} />
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label>SEO title <span className="text-xs text-muted-foreground">(60 chars max)</span></Label><Input value={form.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} maxLength={60} data-testid="blog-seo-title" /></div>
          <div className="sm:col-span-2"><Label>Meta description <span className="text-xs text-muted-foreground">(160 chars max)</span></Label><Textarea rows={2} value={form.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} maxLength={160} data-testid="blog-seo-desc" /></div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Switch checked={!!form.published} onCheckedChange={(v) => set("published", v)} id="pub" data-testid="blog-published" />
        <Label htmlFor="pub" className="cursor-pointer">Published (visible on public blog)</Label>
      </div>

      <div className="flex justify-end gap-3 sticky bottom-0 bg-white border-t border-border pt-4 mt-6 -mx-6 px-6 -mb-4 pb-4">
        <Button variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-saffron hover:bg-saffron-dark text-white" data-testid="blog-save">
          {saving ? "Saving..." : (initial?.id ? "Update" : "Publish")}
        </Button>
      </div>
    </div>
  );
}

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data));
  useEffect(() => {
    load();
    api.get("/products", { params: { limit: 100 } }).then((r) => setProducts(r.data));
  }, []);

  const create = async (data) => {
    try {
      await api.post("/admin/blog", data);
      toast.success("Post created");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Create failed");
      throw err;
    }
  };
  const update = async (data) => {
    try {
      await api.put(`/admin/blog/${editing.id}`, data);
      toast.success("Post updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
      throw err;
    }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/blog/${p.id}`);
      toast.success("Deleted"); load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-blog-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-teal text-white flex items-center justify-center">
            <Newspaper className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-display text-3xl">Blog</h1>
            <p className="text-muted-foreground text-sm">{posts.length} posts · content marketing for organic traffic</p>
          </div>
        </div>
        <Sheet open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
          <SheetTrigger asChild>
            <Button onClick={() => setEditing({})} className="bg-saffron hover:bg-saffron-dark text-white" data-testid="new-post-btn">
              <Plus className="w-4 h-4 mr-2" /> New Post
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-display">{editing?.id ? "Edit post" : "New blog post"}</SheetTitle>
              <SheetDescription>Markdown content, related products, SEO overrides.</SheetDescription>
            </SheetHeader>
            {editing !== null && (
              <PostForm initial={editing} onSubmit={editing.id ? update : create} onCancel={() => setEditing(null)} allProducts={products} />
            )}
          </SheetContent>
        </Sheet>
      </div>

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Cover</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead>Published</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {posts.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-12">No posts yet — create your first article.</TableCell></TableRow>
            ) : posts.map((p) => (
              <TableRow key={p.id} data-testid={`admin-blog-row-${p.slug}`}>
                <TableCell>{p.cover_image ? <img src={p.cover_image} alt="" className="w-14 h-10 rounded object-cover" /> : "—"}</TableCell>
                <TableCell>
                  <div className="font-medium">{p.title}</div>
                  <div className="text-xs text-muted-foreground">{p.slug}</div>
                </TableCell>
                <TableCell><div className="flex gap-1 flex-wrap">{(p.tags || []).slice(0, 3).map((t) => <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>)}</div></TableCell>
                <TableCell>{p.published ? <Badge className="bg-success text-white">Live</Badge> : <Badge variant="outline">Draft</Badge>}</TableCell>
                <TableCell className="text-xs">{p.updated_at?.slice(0, 10)}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => setEditing(p)} data-testid={`edit-post-${p.slug}`}><Edit className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(p)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
